import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Empty, List } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { getProjectListForChat } from '@gtintel/platform-api';
import { LoadingSpinner2, StarIcon } from '@gtintel/platform-atoms';
import { RouteRolesEnum } from '@gtintel/platform-types';
import { countUnreadMessages, getConversationName, getMessageAuthorAvatar, getMessageUserName } from '../../..';
import AttachIcon from '../../../AttachIcon';
import ChatChannelProjectCollapse from '../../molecules/ChatChannelProjectCollapse';
import { ColumnContainer, DateCreated, Description, ListItem, RowContainer, SelectChatListButton, SelectChatListContainer, TitleContainer, UnreadContainer } from './styles';
const getConversationProjectId = conversation => {
  const uniqueName = conversation.uniqueName;
  const convAttrs = conversation.attributes;
  return !convAttrs.project_id && uniqueName.includes('_') ? Number(uniqueName.split('_')[0]) : convAttrs.project_id;
};

// use with .sort() to sort channels in descending order of last message timestamp, nulls last
const cmpConversations = (c1, c2) => {
  const [date1, date2] = [c1, c2].map(conversation => {
    const dateCreated = conversation.lastMessage?.dateCreated;
    return dateCreated ? new Date(dateCreated) : null;
  });
  if (date2 === null) return -1;
  if (date1 === null) return 1;
  return date2.getTime() - date1.getTime();
};

// project ID is undefined if conversation is a DM:

export const getGroupedConversations = conversations => {
  const convsByProjectId = new Map();
  // insert DMs first; Maps preserve insertion order
  convsByProjectId.set(undefined, []);
  conversations.forEach(conversation => {
    const projectId = getConversationProjectId(conversation);
    if (!convsByProjectId.has(projectId)) convsByProjectId.set(projectId, []);
    convsByProjectId.get(projectId).push(conversation);
  });

  // Sort group conversations by last message timestamp (newest first).
  // Sort groups themselves by timestamp of most recent conversation.
  const groups = [...convsByProjectId];
  groups.forEach(group => group[1].sort(cmpConversations));
  groups.sort(([id1, convs1], [id2, convs2]) => {
    if (id2 === undefined) return 1;
    if (id1 === undefined) return -1;
    return cmpConversations(convs1[0], convs2[0]);
  });
  return groups;
};
var Tab = /*#__PURE__*/function (Tab) {
  Tab[Tab["ProjectMessages"] = 0] = "ProjectMessages";
  Tab[Tab["DirectMessages"] = 1] = "DirectMessages";
  return Tab;
}(Tab || {}); // Once conversations are loaded, we still need to load projects and their most
// recent messages. But if there aren't any conversations in the first place,
// then there are no messages to load.
const showSpinner = (conversations, projects, lastMessages) => {
  return conversations === null || projects === null || conversations.length > 0 && _.isEmpty(lastMessages);
};
const ChatChannels = ({
  activeConversationSid,
  conversations,
  currentUserRole,
  openConversation,
  starChannel,
  user
}) => {
  const isLoading = conversations === null;
  const [projects, setProjects] = useState(null);
  const [lastMessages, setLastMessages] = useState({});
  const [expandedPIds, setExpandedPIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState(Tab.ProjectMessages);
  useEffect(() => {
    const getProjects = async () => {
      const projectIds = new Set();
      conversations.forEach(conv => projectIds.add(getConversationProjectId(conv)));
      const projectsResponse = await getProjectListForChat({
        ids: [...projectIds]
      });
      const projects = projectsResponse.reduce((map, project) => map.set(project.id, project), new Map());
      setProjects(projects);
    };
    // Wait till all conversations are loaded from Twilio:
    if (!isLoading) {
      getProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);
  const groups = useMemo(() => {
    if (isLoading) return [];
    return getGroupedConversations(conversations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, conversations?.length]);
  const activeTabConversations = useMemo(() => {
    // Wait until parent component has loaded all conversations from Twilio:
    if (isLoading) return [];
    // DMs are always first because of the way getGroupedConversations works.
    if (activeTab === Tab.DirectMessages) return [groups[0]];
    return groups.slice(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, activeTab, groups.length]);

  // Hackery. We call .getMessages(1) on every conversation (to get the
  // message's author's name because `conversation.lastMessage` doesn't include
  // this). But making all these queries at once can get us throttled by
  // Twilio, so we stagger the queries in batches. The user shouldn't notice
  // the difference because the conversations that aren't loaded immediately
  // aren't visible before they scroll down.
  useEffect(() => {
    if (isLoading) return;
    const loadLastMessages = async conversations => {
      const promises = conversations.map(async conv => {
        const message = await conv.getMessages(1).then(({
          items
        }) => items[0]);
        return [conv.sid, message];
      });
      const data = await Promise.all(promises);
      setLastMessages(prev => {
        return {
          ...prev,
          ...Object.fromEntries(data)
        };
      });
    };
    const conversations = groups.flatMap(group => group[1]);
    const batches = _.chunk(conversations, 100);
    batches.forEach((batch, i) => {
      setTimeout(() => loadLastMessages(batch), i * 200);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.length, isLoading]);
  const toggleGroupCollapse = useCallback(pid => {
    setExpandedPIds(pids => {
      if (pids.has(pid)) {
        pids.delete(pid);
      } else {
        pids.add(pid);
      }
      return pids;
    });
  }, []);
  const renderConversation = useCallback((conversation, project, user) => {
    const chatName = getConversationName({
      conversation,
      routeRole: currentUserRole,
      project
    });
    const message = lastMessages[conversation.sid];
    const avatar = getMessageAuthorAvatar(message, user, project);
    const suggestedPartners = project?.proposals?.map(p => p.partner) ?? [];
    const messageAuthorName = getMessageUserName(message, user, project, suggestedPartners);
    const unreadMessageCount = countUnreadMessages(conversation);
    const hasUnread = !!unreadMessageCount && unreadMessageCount > 0;
    const msgBody = message ? message.body ?? <AttachIcon /> : '';
    return <ListItem className={activeConversationSid === conversation.sid ? 'unRead' : 'read'} key={conversation.sid}>
          <List.Item.Meta avatar={<Avatar src={avatar} />} title={<TitleContainer onClick={() => openConversation(conversation.sid)}>
                <ColumnContainer>
                  <a href="#" className={hasUnread ? 'font-weight-bold' : ''}>
                    {chatName}
                  </a>
                  <Description>
                    {messageAuthorName && `${messageAuthorName}: `}
                    {msgBody}
                  </Description>
                </ColumnContainer>
                <RowContainer>
                  {hasUnread && <UnreadContainer>
                      <span>{unreadMessageCount} unread</span>
                    </UnreadContainer>}
                  <DateCreated>
                    {conversation.lastMessage ? moment(conversation.lastMessage.dateCreated).fromNow() : ''}
                  </DateCreated>
                </RowContainer>
              </TitleContainer>} />
          {activeConversationSid === conversation.sid && currentUserRole === RouteRolesEnum.ADMIN && <StarIcon checked={conversation.attributes.adminStarChannel} clickAction={() => {
        starChannel(conversation);
      }} />}
        </ListItem>;
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [activeConversationSid, openConversation, _.size(lastMessages)]);
  const renderGroup = useCallback(([projectId, groupConversations]) => {
    if (!projectId) {
      return groupConversations.map(c => renderConversation(c, projects.get(projectId), user));
    }

    // The project may have been deleted from the DB although the
    // conversation is still on Twilio.
    const project = projects.get(projectId);
    let projectName = project?.name;
    if (!projectName) {
      const attrs = groupConversations.map(c => c.attributes);
      projectName = attrs.find(a => a.project_name)?.project_name;
    }
    const unreadCount = groupConversations.reduce((acc, conv) => {
      return countUnreadMessages(conv) + acc;
    }, 0);
    return <ChatChannelProjectCollapse conversations={groupConversations} expandedPIds={expandedPIds} key={`collapse_${projectName}_${projectId}`} project={project} projectId={projectId} projectName={projectName} renderConversation={renderConversation} routeRole={currentUserRole} toggleGroupCollapse={toggleGroupCollapse} unreadCount={unreadCount} user={user} />;
  }, [expandedPIds, toggleGroupCollapse, projects, renderConversation, currentUserRole, user]);
  const tabButtons = [[Tab.ProjectMessages, 'Project Messages'], [Tab.DirectMessages, 'Direct Messages']].map(([tab, name], i) => <SelectChatListButton key={i} active={activeTab === tab} onClick={() => setActiveTab(tab)} data-public>
      {name}
    </SelectChatListButton>);
  const isSpinning = showSpinner(conversations, projects, lastMessages);
  const isTabEmpty = !isSpinning && activeTab === Tab.DirectMessages ?
  // There will always be 1 DM 'group' even if its list of convs is empty:
  activeTabConversations[0][1].length === 0 : activeTabConversations.length === 0;
  return <div className="list-chat" data-testid="chat-channels">
      <SelectChatListContainer>{tabButtons}</SelectChatListContainer>
      <div style={{
      height: '90%'
    }}>
        {isSpinning ? <LoadingSpinner2 margin={10} /> : <List dataSource={activeTabConversations} itemLayout="horizontal" renderItem={group => renderGroup(group)}>
            <div style={{
          width: '100%',
          minHeight: 1
        }}>
              {isTabEmpty && <Empty description="Nothing here yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            </div>
          </List>}
      </div>
    </div>;
};
export default ChatChannels;